import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Terminal, Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

// Forgot-password flow: collect an email, ask the backend to email a reset
// link, then flip to a "check your inbox" pane. The server always returns
// 200 (even for unknown emails) to prevent account enumeration, so we never
// surface "email not found" from this screen.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/request-password-reset", { email });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100 flex flex-col">
      <Link to="/" className="flex items-center gap-2 px-6 py-5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#00E5FF] transition-colors">
        <Terminal size={14} /> hypexlabs · CCNA lab practice
      </Link>
      <div className="flex items-center justify-center p-6 flex-1">
        <div className="w-full max-w-sm">
          {sent ? (
            <div className="space-y-5" data-testid="forgot-sent">
              <CheckCircle2 className="text-[#00FF41]" size={36} />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00FF41] font-mono">check your inbox</div>
              <h1 className="font-display font-black text-3xl tracking-tight">Reset link sent</h1>
              <p className="text-zinc-400 text-sm leading-relaxed">
                If <span className="text-zinc-200 font-mono">{email}</span> is on file, we just sent a password-reset link there. The link expires in 60 minutes.
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#00E5FF]" data-testid="forgot-back-to-login">
                <ArrowLeft size={12} /> back to login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">recover</div>
              <h1 className="font-display font-black text-3xl tracking-tight mb-2">Forgot password?</h1>
              <p className="text-zinc-500 text-sm font-mono mb-6">No problem. Enter your email and we'll send you a reset link.</p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">email</label>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                    <input
                      type="email" required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                      placeholder="you@example.com"
                      data-testid="forgot-email"
                    />
                  </div>
                </div>
                {err && <div className="text-xs text-[#EF4444] font-mono" data-testid="forgot-error">{err}</div>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-[#00E5FF] hover:bg-[#00b8cc] text-black text-xs font-mono uppercase tracking-widest font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="forgot-submit"
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                  {loading ? "sending…" : "send reset link"}
                </button>
                <Link to="/login" className="block text-center text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-[#00E5FF] pt-2" data-testid="forgot-back">
                  back to login
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
